import React, { memo, MouseEvent, useCallback, useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableContainer from '@mui/material/TableContainer'
import { Column } from '../../../shared/types/Column'
import { useAppDispatch } from '../../../shared/lib/hooks/useAppDispatch/useAppDispatch'
import { useAppSelector } from '../../../shared/lib/hooks/useAppSelector/useAppSelector'
import { MuiTableHead } from '../../../shared/ui/MuiTableHead/MuiTableHead'
import { selectLicenseId } from '../../License'
import TablePagination from '@mui/material/TablePagination'
import { selectLimit } from '../model/selectors/selectLimit'
import { selectSortDirection } from '../model/selectors/selectSortDirection'
import { selectPage } from '../model/selectors/selectPage'
import { selectSort } from '../model/selectors/selectSort'
import { selectTotalCount } from '../model/selectors/selectTotalCount'
import { InstanceEventsTable } from './LicenseEventsTable/InstanceEventsTable'
import { instanceEventsActions } from '../model/slices/InstanceEventsSlice'
import { fetchInstanceEvents } from '../model/services/fetchInstanceEvents'
import {
	InstanceEventsFilterChipList
} from './InstanceEventsFilter/InstanceEventsFilterChipList'
import { InstanceEventsFilter } from './InstanceEventsFilter/InstanceEventsFilter'
import { useParams } from 'react-router-dom'

const columns: Column[] = [
	{ id: 'updateTime', label: 'Update Time', sortable: true, align: 'right' },
	{ id: 'instanceName', label: 'Instance name' },
	{ id: 'eventName', label: 'Event' },
	{ id: 'content', label: 'Content' }
]

export const InstanceEventsUi = memo(
	function InstanceEventsUi() {
		const dispatch = useAppDispatch()
		const isFirstOpen = useRef(true)
		const licenseIdFromParams = useParams().licenseId
		const licenseId = useAppSelector(selectLicenseId)
		const totalCount = useAppSelector(selectTotalCount)
		const currentPage = useAppSelector(selectPage)
		const limit = useAppSelector(selectLimit)
		const direction = useAppSelector(selectSortDirection)
		const sort = useAppSelector(selectSort)

		const handleChangePage = (_event: unknown, newPage: number) => {
			dispatch(instanceEventsActions.setPage(newPage + 1))
		}

		const handleChangeRowsPerPage = (
			event: React.ChangeEvent<HTMLInputElement>
		) => {
			dispatch(instanceEventsActions.setLimit(+event.target.value))
			dispatch(instanceEventsActions.setPage(1))
		}

		const handleSort = useCallback((
			event: MouseEvent<unknown>,
			property: any
		) => {
			event.preventDefault()
			const isAsc = sort === property && direction === 'ascending'
			dispatch(instanceEventsActions.setDirection(isAsc ? 'descending' : 'ascending'))
			dispatch(instanceEventsActions.setSort(property))
			dispatch(instanceEventsActions.resetPage())
		}, [dispatch, sort, direction])

		useEffect(() => {
			if (isFirstOpen.current) {
				const sevenDaysAgo = new Date()
				sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
				dispatch(instanceEventsActions.setUpdateTimeFrom(sevenDaysAgo.toISOString()))
				dispatch(instanceEventsActions.setQueryFilter())
				isFirstOpen.current = false
			}
			if (licenseIdFromParams) {
				dispatch(fetchInstanceEvents(licenseIdFromParams))
			}
		}, [dispatch, licenseIdFromParams])

		return (
			<Box>
				<InstanceEventsFilter />
				<InstanceEventsFilterChipList licenseId={licenseId} />
				{totalCount ? (
					<>
						<TableContainer sx={{ maxHeight: window.innerHeight * 0.44 }}>
							<Table aria-label="sticky table">
								<MuiTableHead
									flag={false}
									orderBy={sort}
									columns={columns}
									onRequestSort={handleSort}
									order={direction === 'descending' ? 'desc' : 'asc'}
								/>
								<InstanceEventsTable columns={columns} />
							</Table>
						</TableContainer>
						<TablePagination
							component="div"
							rowsPerPage={+limit}
							rowsPerPageOptions={[10, 25, 50]}
							count={totalCount}
							page={currentPage === 0 ? 0 : currentPage - 1}
							onRowsPerPageChange={handleChangeRowsPerPage}
							onPageChange={handleChangePage}
						/>
					</>
				) : (
					<Box
						sx={{
							p: '2%',
							opacity: '0.7',
							fontSize: '1.2rem',
							textAlign: 'center',
							borderRadius: '8px',
							backgroundColor: '#0000000A'
						}}
					>
						<i>No instance events to display</i>
					</Box>
				)}
			</Box>
		)
	})
